import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createFolderApi,
  getFoldersByModeApi,
  getFolderByIdApi,
  getTrashFoldersApi,
  getStarredFoldersApi,
  updateFolderApi,
  uploadFolderImageApi,
  deleteAllFoldersApi,
  softDeleteFolderApi,
  deleteFolderPermanentlyApi,
  deleteMultipleFoldersApi,
  markFolderAsStarredApi,
  unmarkFolderAsStarredApi,
  restoreFolderApi,
  toggleFolderStatus,
  shareMultipleFoldersApi,
  getSharedFoldersApi,
  removeSharedFolderApi
} from "../Api/folderApi";

// TYPES ----------------------------------------------------------------------

export interface Folder {
  _id: string;
  Name: string;
  status?: string;
  is_deleted?: boolean;
  is_starred?: boolean;
  is_activated?: boolean;
  [key: string]: any;
}

export interface FolderResponse {
  data: Folder[];
  [key: string]: any;
}

interface FolderState {
  sharedFolders: Folder[];
  folders: FolderResponse | null;
  folderDetail: Folder | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

const initialState: FolderState = {
  sharedFolders: [],
  folders: null,
  folderDetail: null,
  loading: false,
  actionLoading: false,
  error: null,
};


interface UploadImagePayload {
  id: string;
  image: File;
}

interface CreateFolderPayload {
  mode: string;
  user: string;
  Name: string;
  description?: string;
  status?: string;
  metadata?: any;
}

interface FetchFoldersParams {
  modeid: string;
  page_no: number;
  page_size: number;
  userId: string
}

// HELPERS --------------------------------------------------------------------

const removeFolder = (state: FolderState, id: string) => {
  if (!state.folders?.data) return;
  state.folders.data = state.folders.data.filter((f) => f._id !== id);
};

const updateFolderInState = (state: FolderState, updated: Folder) => {
  if (!state.folders?.data) return;
  state.folders.data = state.folders.data.map((f) =>
    f._id === updated._id ? updated : f
  );
};

// THUNKS ---------------------------------------------------------------------
export const createFolderWithImage = createAsyncThunk(
  "folders/createWithImage",
  async (
    { folderData, imageFile }: { folderData: CreateFolderPayload; imageFile?: File },
    { rejectWithValue }
  ) => {
    try {
      // STEP 1: CREATE FOLDER
      const created = await createFolderApi(folderData);
      const folder = created?.data;

      // STEP 2: UPLOAD IMAGE (if provided)
      let updatedFolder = folder;
      if (imageFile && folder?._id) {
        const uploadRes = await uploadFolderImageApi({
          id: folder._id,
          image: imageFile,
        });

        updatedFolder = uploadRes?.data;
      }

      return updatedFolder;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create folder with image"
      );
    }
  }
);


export const fetchFoldersByMode = createAsyncThunk(
  "folders/getAll",
  async ({ modeid, page_no, page_size, userId }: FetchFoldersParams) => {
    const res = await getFoldersByModeApi(modeid, userId, page_no, page_size);
    return res.data || [];
  }
);

export const fetchFolderById = createAsyncThunk(
  "folders/getById",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await getFolderByIdApi(id);
      return res.data && res.data.length > 0 ? res.data[0] : null;
    } catch (error) {
      return rejectWithValue("Failed to fetch folder");
    }
  }
);

export const fetchTrashFolders = createAsyncThunk(
  "folders/getTrash",
  async ({ modeid, page_no, page_size, userId }: FetchFoldersParams) => {
    const res = await getTrashFoldersApi(modeid, userId, page_no, page_size);
    return res.data;
  }
);

export const fetchStarredFolders = createAsyncThunk(
  "folders/getStarred",
  async ({ modeid, page_no, page_size, userId }: FetchFoldersParams) => {
    const res = await getStarredFoldersApi(modeid, userId, page_no, page_size);
    return res;
  }
);

export const updateFolder = createAsyncThunk(
  "folders/update",
  async (payload: { id: string; modify: any }) => {
    const res = await updateFolderApi(payload);
    return res.data; // backend returns data: updatedFolder
  }
);

export const uploadFolderImage = createAsyncThunk(
  "folders/uploadImage",
  async (payload: { id: string; image: File }, { rejectWithValue }) => {
    try {
      const res = await uploadFolderImageApi(payload);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload image');
    }
  }
);

export const deleteAllFolders = createAsyncThunk(
  "folders/deleteAll",
  async (modeId: string) => {
    const res = await deleteAllFoldersApi(modeId);
    return res;
  }
);

export const softDeleteFolder = createAsyncThunk(
  "folders/softDelete",
  async (id: string) => {
    await softDeleteFolderApi(id);
    return id; // delete by ID (backend returns no folder)
  }
);

export const deleteFolderPermanently = createAsyncThunk(
  "folders/deletePermanent",
  async (id: string) => {
    await deleteFolderPermanentlyApi(id);
    return id; // delete by ID
  }
);

export const deleteMultipleFolders = createAsyncThunk(
  "folders/deleteMultiple",
  async (folderIds: string[]) => {
    const res = await deleteMultipleFoldersApi(folderIds);
    return folderIds; // backend does not return ids → use input
  }
);

// SHARE MULTIPLE
export const shareMultipleFolders = createAsyncThunk(
  "folders/shareMultiple",
  async (data: { folderIds: string[]; userIds: string[] }) => {
    const res = await shareMultipleFoldersApi(data);
    return res.data || res;
  }
);

export const markFolderAsStarred = createAsyncThunk(
  "folders/star",
  async (id: string) => {
    const res = await markFolderAsStarredApi(id);
    return res.data; // updated folder
  }
);

export const unmarkFolderAsStarred = createAsyncThunk(
  "folders/unstar",
  async (id: string) => {
    const res = await unmarkFolderAsStarredApi(id);
    return res.data; // updated folder
  }
);

export const restoreFolder = createAsyncThunk(
  "folders/restore",
  async (id: string) => {
    const res = await restoreFolderApi(id);
    return res.data; // restored folder object
  }
);

export const toggleFolderSlice = createAsyncThunk(
  "folders/toggleStatus",
  async (id: string) => {
    const res = await toggleFolderStatus(id);
    return res.data.folder; // backend returns { message, folder }
  }
);

export const fetchSharedFolders = createAsyncThunk(
  "shared/fetchFolders",
  async ({ userId, page_no, page_size }: { userId: string; page_no?: number; page_size?: number }, { rejectWithValue }) => {
    try {
      const res = await getSharedFoldersApi(userId, page_no, page_size);
      return res.data || res;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch shared folders");
    }
  }
);

export const removeSharedFolder = createAsyncThunk(
  "shared/removeFolder",
  async ({ folderId, userId }: { folderId: string; userId: string }, { rejectWithValue }) => {
    try {
      await removeSharedFolderApi(folderId, userId);
      return folderId;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to remove shared folder");
    }
  }
);


// SLICE ----------------------------------------------------------------------

const folderSlice = createSlice({
  name: "folders",
  initialState,
  reducers: {
    clearFolderDetail: (state) => {
      state.folderDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // CREATE ----------------------------------------------------------------
      .addCase(createFolderWithImage.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createFolderWithImage.fulfilled, (state, action) => {
        const newFolder = action.payload;

        if (!newFolder || typeof newFolder !== "object") return;
        state.actionLoading = false;

        // Insert new folder at TOP of the list in UI
        if (!state.folders) {
          state.folders = { data: [newFolder] };
        } else if (Array.isArray(state.folders.data)) {
          const exists = state.folders.data.some(f => f?._id === newFolder._id);
          if (!exists) state.folders.data.unshift(newFolder);
        }

        state.folderDetail = newFolder;
      })
      .addCase(createFolderWithImage.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string || "Failed to create folder";
      })

      // FETCH LIST ------------------------------------------------------------
      .addCase(fetchFoldersByMode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFoldersByMode.fulfilled, (state, action) => {
        const rawData = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
        state.folders = { data: rawData };
        state.loading = false;
      })

      .addCase(fetchFoldersByMode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load folders";
      })

      .addCase(fetchStarredFolders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStarredFolders.fulfilled, (state, action) => {
        const rawData = action.payload?.Starred || action.payload?.data || (Array.isArray(action.payload) ? action.payload : []);
        state.folders = { data: rawData };
        state.loading = false;
      })

      .addCase(fetchStarredFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load starred folders";
      })

      // FETCH BY ID -----------------------------------------------------------
      .addCase(fetchFolderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFolderById.fulfilled, (state, action) => {
        // The thunk now returns the folder directly, not wrapped in an array
        state.folderDetail = action.payload;
        if (action.payload) {
          updateFolderInState(state, action.payload);
        }
        state.loading = false;
      })
      .addCase(fetchFolderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch folder";
      })

      // UPDATE FOLDER ---------------------------------------------------------
      .addCase(updateFolder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const updated = action.payload;
        state.actionLoading = false;
        if (state.folders?.data) {
          state.folders.data = state.folders.data.map((f) =>
            f._id === updated._id ? { ...updated, user: f.user } : f
          );
        }
        state.folderDetail = updated;
      })
      .addCase(updateFolder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to update folder";
      })

      // SOFT DELETE -----------------------------------------------------------
      .addCase(softDeleteFolder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(softDeleteFolder.fulfilled, (state, action) => {
        state.actionLoading = false;
        removeFolder(state, action.payload);
      })
      .addCase(softDeleteFolder.rejected, (state) => {
        state.actionLoading = false;
      })

      // PERMANENT DELETE ------------------------------------------------------
      .addCase(deleteFolderPermanently.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteFolderPermanently.fulfilled, (state, action) => {
        state.actionLoading = false;
        removeFolder(state, action.payload);
      })
      .addCase(deleteFolderPermanently.rejected, (state) => {
        state.actionLoading = false;
      })

      // DELETE MULTIPLE -------------------------------------------------------
      .addCase(deleteMultipleFolders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteMultipleFolders.fulfilled, (state, action) => {
        state.actionLoading = false;
        action.payload.forEach((id) => removeFolder(state, id));
      })
      .addCase(deleteMultipleFolders.rejected, (state) => {
        state.actionLoading = false;
      })

      // STAR / UNSTAR ---------------------------------------------------------
      .addCase(markFolderAsStarred.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(markFolderAsStarred.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (state.folders?.data) {
          state.folders.data = state.folders.data.map((f) =>
            f._id === updated._id ? { ...updated, user: f.user } : f
          );
        }
        if (state.folderDetail && state.folderDetail._id === updated._id) {
          state.folderDetail.is_starred = updated.is_starred;
        }
      })
      .addCase(markFolderAsStarred.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(unmarkFolderAsStarred.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(unmarkFolderAsStarred.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (state.folders?.data) {
          state.folders.data = state.folders.data.map((f) =>
            f._id === updated._id ? { ...updated, user: f.user } : f
          );
        }
        if (state.folderDetail && state.folderDetail._id === updated._id) {
          state.folderDetail.is_starred = updated.is_starred;
        }
      })
      .addCase(unmarkFolderAsStarred.rejected, (state) => {
        state.actionLoading = false;
      })

      // RESTORE (REMOVE FROM TRASH LIST) --------------------------------------
      .addCase(restoreFolder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(restoreFolder.fulfilled, (state, action) => {
        state.actionLoading = false;
        removeFolder(state, action.payload._id);
      })
      .addCase(restoreFolder.rejected, (state) => {
        state.actionLoading = false;
      })

      // STATUS TOGGLE ---------------------------------------------------------
      .addCase(toggleFolderSlice.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(toggleFolderSlice.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (state.folders?.data) {
          state.folders.data = state.folders.data.map((f) =>
            f._id === updated._id ? { ...f, ...updated, user: f.user } : f
          );
        }
        if (state.folderDetail && state.folderDetail._id === updated._id) {
          state.folderDetail = { ...state.folderDetail, ...updated, user: state.folderDetail.user };
        }
      })
      .addCase(toggleFolderSlice.rejected, (state) => {
        state.actionLoading = false;
      })

      // TRASH FOLDER
      .addCase(fetchTrashFolders.fulfilled, (state, action) => {
        const foldersArray = Array.isArray(action.payload) ? action.payload : [];

        const normalized = foldersArray.map((folder: Folder) => ({
          ...folder,
          user: Array.isArray(folder.user) ? folder.user[0] : folder.user
        }));

        state.folders = { data: normalized };
        state.loading = false;
      })
      .addCase(fetchTrashFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrashFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load trash folders";
      })

      //UPLOAD IMAGE
      .addCase(uploadFolderImage.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(uploadFolderImage.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload?.folder || action.payload?.data;

        if (!updated?._id) return;

        updateFolderInState(state, updated);
        state.folderDetail = updated;
      })
      .addCase(uploadFolderImage.rejected, (state) => {
        state.actionLoading = false;
      })

      // FETCH FOLDERS
      .addCase(fetchSharedFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSharedFolders.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedFolders = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
      })
      .addCase(fetchSharedFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // REMOVE FOLDER
      .addCase(removeSharedFolder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(removeSharedFolder.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.sharedFolders = state.sharedFolders.filter((f) => f._id !== action.payload);
      })
      .addCase(removeSharedFolder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })

  },
});

export const { clearFolderDetail } = folderSlice.actions;
export default folderSlice.reducer;
